import { type WorkoutSliderItem } from "@fitness-app/data-models/entities/LiveWorkout";
import {
  type ExerciseDuration,
  type ExerciseInWorkout,
  type ExerciseSet,
  type ProgramWorkout,
} from "@fitness-app/data-models/entities/TrainingProgram";
import { getRestAfterExercise } from "@fitness-app/utils/src/programs/getRestAfterExercise";

/** Tempo of a single repeat, including the eccentric part. */
export const SECONDS_PER_REPEAT = 4;
/** Getting into position, setting the weight and logging the result - paid once per set. */
export const SET_SETUP_SECONDS = 20;
/** Repeats assumed when the trainer left the field empty. */
const DEFAULT_REPEATS = 10;

/** Upper bound of a "8-12" range: the estimate should not promise the shorter end. */
const upperBound = (value: number | [number, number] | null | undefined) =>
  Array.isArray(value) ? (value[1] ?? value[0] ?? null) : (value ?? null);

/** 0 is the admin form's default for an unset duration, so it counts as "not timed". */
const durationSeconds = (duration: ExerciseDuration | null | undefined) => {
  const value = upperBound(duration?.value);
  if (!value) return null;
  return duration?.format === "minutes" ? value * 60 : value;
};

/**
 * Rough duration of one set in seconds. A timed set (plank, warm-up "10 min") lasts its duration
 * times its repeats (10 × 30 s intervals; the form defaults timed exercises to 1 repeat), the same
 * as `exerciseVolume` - it used to count as a single 3 s repeat, which is what made a 60-minute
 * plan read as 30 (TRE-2351).
 */
export const estimateSetSeconds = (
  set: Pick<ExerciseSet, "repeats" | "duration">,
  fallbackDuration?: ExerciseDuration | null,
) => {
  const repeats = upperBound(set.repeats);
  const timed = durationSeconds(set.duration) ?? durationSeconds(fallbackDuration);
  const work = timed ? timed * (repeats || 1) : (repeats || DEFAULT_REPEATS) * SECONDS_PER_REPEAT;
  return work + SET_SETUP_SECONDS;
};

/**
 * Rough live workout duration in seconds: every set (its duration, or its repeats at a steady tempo,
 * plus setup) and every rest. The slider already holds one item per set (`series` is the set's index),
 * so a set counts once.
 */
export const calcWorkoutTime = (workoutData: WorkoutSliderItem[]) =>
  workoutData.reduce((prev, current) => {
    if ("repeats" in current && "series" in current) {
      return prev + estimateSetSeconds(current, current.exerciseInWorkout?.duration);
    }
    if ("restTime" in current) {
      return prev + current.restTime;
    }
    return prev;
  }, 0);

/**
 * Rough duration of a planned workout in seconds - the same per-set arithmetic as calcWorkoutTime,
 * but on the plain plan: set duration or repeats plus setup, rests between series and the rest after
 * each exercise (a superset rests once, after its last exercise; nothing after the last exercise).
 * Inside a superset every exercise but the last rests after each of its series, like the live workout.
 */
export const estimateWorkoutSeconds = (
  workout: Pick<ProgramWorkout, "preWorkout" | "exercises" | "postWorkout" | "restBetweenExercises">,
) => {
  // superSetId is unique only within a workout part (warm-up and main part can both have superset 1).
  const parts: ExerciseInWorkout[][] = [workout.preWorkout ?? [], workout.exercises ?? [], workout.postWorkout ?? []];
  const exercises = parts.flatMap((part, partIndex) => part.map((exercise) => ({ exercise, partIndex })));
  const supersetKey = (partIndex: number, superSetId: number) => `${partIndex}:${superSetId}`;
  const lastInSuperSet = new Map(
    exercises
      .filter(({ exercise }) => exercise.superSetId)
      .map(({ exercise, partIndex }) => [supersetKey(partIndex, exercise.superSetId!), exercise.id]),
  );
  return exercises.reduce((total, { exercise, partIndex }, index) => {
    const series = exercise.numberOfSeries || 1;
    const work = estimateSetSeconds({ repeats: exercise.numberOfRepeats ?? null, duration: exercise.duration });
    const closesItem =
      !exercise.superSetId || lastInSuperSet.get(supersetKey(partIndex, exercise.superSetId)) === exercise.id;
    const rests = (closesItem ? Math.max(0, series - 1) : series) * (exercise.restTime ?? 0);
    const after = index < exercises.length - 1 && closesItem ? getRestAfterExercise(exercise, workout) : 0;
    return total + series * work + rests + after;
  }, 0);
};
